import { useGetOGPUserInfo } from 'ogp/hooks';
import { routePrefix as ogpPrefix } from 'ogp/settings';
import { partnerRoutePrefix } from 'partner/settings';
import { generatePath, Navigate } from 'react-router-dom';
import { hqPrefix } from 'Routes';
import { useFeatureFlagContext } from 'shared/contexts/feature-flag-provider';
import { useSiteDetection } from 'shared/hooks';
import { NotFound } from 'shared/components/views/404/404';
import { FeatureFlagGuardProps, PlatformRedirectGuardProps } from './feature-flag-guard.types';

export const FeatureFlagGuard = ({
  flags,
  mode = 'any',
  fallbackPath,
  children,
}: FeatureFlagGuardProps) => {
  const { isHQ, isOGP, isPartner, site } = useSiteDetection();

  const featureFlags = useFeatureFlagContext();

  if (!flags || flags.length === 0) {
    return <>{children}</>;
  }

  const activeFlags = flags.map((flag) => !!featureFlags[flag]);
  const isAllowed = mode === 'all' ? activeFlags.every(Boolean) : activeFlags.some(Boolean);

  if (isAllowed) {
    return <>{children}</>;
  }

  if (isHQ) {
    return <FeatureFlagHqGuard fallbackPath={fallbackPath} />;
  }

  if (isOGP) {
    return <FeatureFlagOgpGuard fallbackPath={fallbackPath} />;
  }

  if (isPartner) {
    return <FeatureFlagPartnerGuard fallbackPath={fallbackPath} />;
  }

  return <NotFound message={`Unsupported platform: ${site}`} />;
};

const FeatureFlagHqGuard = ({ fallbackPath }: PlatformRedirectGuardProps) => {
  const redirectPath = `${hqPrefix}/${fallbackPath ?? ''}`;

  return <Navigate to={redirectPath} replace />;
};

const FeatureFlagOgpGuard = ({ fallbackPath }: PlatformRedirectGuardProps) => {
  const userQuery = useGetOGPUserInfo();
  const redirectPath = `${ogpPrefix}/:companyHash/${fallbackPath ?? ''}`;
  const to = generatePath(redirectPath, { companyHash: userQuery.data?.userInfo.company.slug });

  return <Navigate to={to} replace />;
};

const FeatureFlagPartnerGuard = ({ fallbackPath }: PlatformRedirectGuardProps) => {
  const redirectPath = `${partnerRoutePrefix}/${fallbackPath ?? ''}`;

  return <Navigate to={redirectPath} replace />;
};
